import { createContext, ReactNode, useCallback, useMemo, useState } from "react";
import SysTable from "app/class/sys-table";
import { LogicalNode } from "app/types/record-management-types";
import { RecordPermissions } from "modules/record/shared/services/actionPermissions";

const areStringArraysEqual = (left: string[], right: string[]) => {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((value, index) => value === right[index]);
};

const areRowArraysEqualById = (left: any[], right: any[]) => {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((row, index) => row?.id === right[index]?.id);
};

export interface RecordListContextState {
    table: SysTable | null;
    selectedIds: string[];
    selectedRows: any[];
    queryParams?: LogicalNode;
    permissions?: RecordPermissions;
    readDenied: boolean;
    setPermissions(permissions?: RecordPermissions): void;
    setReadDenied(denied: boolean): void;
    setSelection(ids: string[], rows: any[]): void;
    clearSelection(): void;
    reload(): void;
}

const RecordListContext =
    createContext<RecordListContextState | null>(null);

interface RecordListProviderProps {
    table: SysTable | null;
    queryParams?: any;
    onReload?: () => void;
    children: ReactNode;
}

const RecordListProvider = ({
    table,
    queryParams,
    onReload,
    children
}: RecordListProviderProps) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [permissions, setPermissions] = useState<RecordPermissions | undefined>();
    const [readDenied, setReadDenied] = useState(false);

    const setSelection = useCallback(
        (ids: string[], rows: any[]) => {
            setSelectedIds((currentIds) => (
                areStringArraysEqual(currentIds, ids) ? currentIds : ids
            ));
            setSelectedRows((currentRows) => (
                areRowArraysEqualById(currentRows, rows) ? currentRows : rows
            ));
        }, []
    );

    const clearSelection = useCallback(() => {
        setSelectedIds((currentIds) => (currentIds.length === 0 ? currentIds : []));
        setSelectedRows((currentRows) => (currentRows.length === 0 ? currentRows : []));
    }, []);

    const reload = useCallback(() => {
        clearSelection();
        onReload?.();
    }, [onReload, clearSelection]);

    const value = useMemo(
        () => ({
            table,
            selectedIds,
            selectedRows,
            queryParams,
            permissions,
            readDenied,
            setPermissions,
            setReadDenied,
            setSelection,
            clearSelection,
            reload
        }),
        [
            table,
            selectedIds,
            selectedRows,
            queryParams,
            permissions,
            readDenied,
            setSelection,
            clearSelection,
            reload
        ]
    );

    return (
        <RecordListContext.Provider value={value}>
            {children}
        </RecordListContext.Provider>
    );
};

export default RecordListProvider;
export { RecordListContext, RecordListProvider }
